import { theme, formatMoney, showToast } from './global.js';
import { addToCart } from './cart.js';

// Cart page behaviour. The quantity, remove and add buttons are handled by cart.js; this file refreshes the page
// section after every change, shows the emirate delivery estimate, saves the order note and offers "Undo" on remove.
const EMIRATE_KEY = 'energy:emirate';
const normalise = (name) => String(name || '').toLowerCase().replace(/al[- ]/g, '').replace(/\s/g, '');
const money = (cents) => formatMoney(cents, theme.moneyFormat).replace('Dhs.', 'AED');
const cartUrl = () => theme.routes.cart || '/cart';

let busyTimer = null;
let lastRemoved = null;

const root = () => document.querySelector('[data-cart-page]');

function storedEmirate() {
  try {
    return localStorage.getItem(EMIRATE_KEY) || 'Dubai';
  } catch (error) {
    return 'Dubai';
  }
}

function setBusy(on) {
  const el = root();
  if (!el) return;
  el.classList.toggle('is-updating', on);
  clearTimeout(busyTimer);
  // Never leave the page dimmed if an update fails.
  if (on) busyTimer = setTimeout(() => refresh(), 8000);
}

// Delivery estimate for the chosen emirate, and the total including it.
function renderEstimate() {
  const el = root();
  if (!el) return;
  const emirate = storedEmirate();
  const label = emirate.replace('al-', 'Al ');
  let charges = {};
  try {
    charges = JSON.parse(el.dataset.charges || '{}');
  } catch (error) {
    charges = {};
  }
  const amount = charges[normalise(emirate)];
  const total = Number(el.dataset.totalCents) || 0;

  el.querySelectorAll('[data-cart-emirate-name]').forEach((node) => { node.textContent = label; });
  const select = el.querySelector('[data-cart-emirate]');
  if (select && Array.from(select.options).some((o) => o.value === emirate)) select.value = emirate;

  const chargeEl = el.querySelector('[data-cart-delivery-charge]');
  if (typeof amount === 'number') {
    if (chargeEl) chargeEl.textContent = money(amount * 100);
    el.querySelectorAll('[data-cart-total-value]').forEach((node) => { node.textContent = money(total + amount * 100); });
  } else {
    if (chargeEl) chargeEl.textContent = 'Calculated at checkout';
    el.querySelectorAll('[data-cart-total-value]').forEach((node) => { node.textContent = money(total); });
  }
}

function setEmirate(emirate) {
  const headerOption = document.querySelector(`[data-emirate-option="${emirate}"]`);
  if (headerOption) {
    // The header picker saves the choice, updates the cart attribute and announces it.
    headerOption.click();
    return;
  }
  try {
    localStorage.setItem(EMIRATE_KEY, emirate);
  } catch (error) {
    /* storage unavailable: the choice still applies to this page view */
  }
  fetch(theme.routes.cartUpdate || '/cart/update.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ attributes: { Emirate: emirate.replace('al-', 'Al ') } }),
  }).catch(() => {});
  renderEstimate();
}

async function refresh() {
  const current = root();
  if (!current) return;
  const note = current.querySelector('#CartNote');
  const noteState = note ? { value: note.value, open: note.closest('details')?.open } : null;
  try {
    const res = await fetch(`${cartUrl()}?section_id=${encodeURIComponent(current.dataset.sectionId)}`, { headers: { Accept: 'text/html' } });
    const html = await res.text();
    const incoming = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-cart-page]');
    if (incoming) {
      current.replaceWith(incoming);
      const freshNote = incoming.querySelector('#CartNote');
      if (freshNote && noteState) {
        freshNote.value = noteState.value;
        const details = freshNote.closest('details');
        if (details && noteState.open) details.open = true;
      }
    }
  } catch (error) {
    /* keep the current view; the next change retries */
  }
  setBusy(false);
  renderEstimate();
  syncWishlistButtons();
  offerUndo();
}

function syncWishlistButtons() {
  // The section was replaced, so mark the fresh "Save" buttons that are already on the wishlist.
  let saved = [];
  try {
    saved = JSON.parse(localStorage.getItem('energy:wishlist') || '[]');
  } catch (error) {
    saved = [];
  }
  document.querySelectorAll('[data-cart-page] [data-wishlist-toggle]').forEach((btn) => {
    btn.setAttribute('aria-pressed', saved.includes(btn.dataset.handle));
  });
}

function offerUndo() {
  if (!lastRemoved) return;
  const item = lastRemoved;
  lastRemoved = null;
  document.querySelector('.cart-undo')?.remove();
  const bar = document.createElement('div');
  bar.className = 'cart-undo';
  bar.setAttribute('role', 'status');
  bar.innerHTML = '<span></span><button type="button">Undo</button>';
  bar.firstChild.textContent = `${item.title} removed`;
  Object.assign(bar.style, {
    position: 'fixed', insetInline: '1rem', insetBlockEnd: '5.5rem', zIndex: '70', maxWidth: '26rem', marginInline: 'auto',
    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem', padding: '0.75rem 1rem',
    borderRadius: '999px', background: '#111', color: '#fff', fontSize: '0.88rem', boxShadow: '0 14px 30px -12px rgba(0,0,0,0.5)',
  });
  Object.assign(bar.firstChild.style, { minWidth: '0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' });
  Object.assign(bar.lastChild.style, { background: '#f0f000', color: '#2a1a73', border: '0', borderRadius: '999px', padding: '0.35rem 0.9rem', fontWeight: '800', cursor: 'pointer' });
  bar.lastChild.addEventListener('click', async () => {
    bar.remove();
    const data = new FormData();
    data.append('id', item.variantId);
    data.append('quantity', item.quantity);
    await addToCart(data, { openDrawer: false });
  });
  document.body.appendChild(bar);
  setTimeout(() => bar.remove(), 7000);
}

/* ---- Events (bound once; the section element itself is replaced on refresh) ---- */
document.addEventListener('click', (event) => {
  const el = root();
  if (!el || !el.contains(event.target)) return;
  const removeBtn = event.target.closest('[data-cart-remove]');
  if (removeBtn) {
    const line = removeBtn.closest('[data-cart-item]');
    lastRemoved = {
      variantId: line?.dataset.variantId,
      quantity: Number(line?.dataset.quantity) || 1,
      title: line?.dataset.title || 'Item',
    };
    setBusy(true);
    return;
  }
  if (event.target.closest('[data-quantity-decrease], [data-quantity-increase]')) setBusy(true);
}, true);

document.addEventListener('change', (event) => {
  const el = root();
  if (!el || !el.contains(event.target)) return;
  if (event.target.matches('[data-quantity-input]')) {
    setBusy(true);
    return;
  }
  if (event.target.matches('[data-cart-emirate]')) {
    setEmirate(event.target.value);
    return;
  }
  if (event.target.id === 'CartNote') {
    fetch(theme.routes.cartUpdate || '/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ note: event.target.value }),
    }).catch(() => {});
  }
}, true);

document.addEventListener('cart:sections-updated', () => { if (root()) refresh(); });
document.addEventListener('emirate:change', renderEstimate);
renderEstimate();
